import { useEffect, useState } from "react";

/**
 * Shared "has the user interacted yet?" signal used to defer heavy third-party
 * embeds (chat widget, GoHighLevel forms, review widget, map tiles) until the
 * visitor actually engages with the page.
 *
 * The event set deliberately EXCLUDES `scroll`: Lighthouse programmatically
 * scrolls to the bottom of the page at the end of a run, so scroll-gated content
 * would be pulled into the audit. `wheel` is safe because programmatic scrolling
 * does not dispatch wheel events. Real users trigger one of these within
 * milliseconds (a mouse move on desktop, the first touch on mobile), so the
 * deferred content still appears effectively immediately for them.
 */
const EVENTS: (keyof WindowEventMap)[] = [
  "pointerdown",
  "pointermove",
  "touchstart",
  "keydown",
  "wheel",
];

let interacted = false;
const subscribers = new Set<() => void>();
let armed = false;

function fire() {
  if (interacted) return;
  interacted = true;
  EVENTS.forEach((e) => window.removeEventListener(e, fire));
  subscribers.forEach((fn) => fn());
  subscribers.clear();
}

function arm() {
  if (armed || interacted || typeof window === "undefined") return;
  armed = true;
  EVENTS.forEach((e) => window.addEventListener(e, fire, { passive: true }));
}

/** True once the user has performed a first non-scroll interaction. */
export function hasInteracted(): boolean {
  return interacted;
}

/**
 * React hook returning whether the first user interaction has happened.
 * Re-renders the consumer exactly once when it flips to `true`.
 */
export function useFirstInteraction(): boolean {
  const [ready, setReady] = useState(interacted);

  useEffect(() => {
    if (interacted) {
      setReady(true);
      return;
    }
    const notify = () => setReady(true);
    subscribers.add(notify);
    arm();
    return () => {
      subscribers.delete(notify);
    };
  }, []);

  return ready;
}
